/**
 * What name the public sees for a seller.
 *
 * An agency or a development company trades under its own name, not the name on
 * the director's ID. A buyer browsing listings should see "Palm Leaf Real
 * Estate", not "Tendai Moyo". Solo agents and FSBO landlords are the opposite:
 * they ARE the brand, so their own name is what builds their reputation.
 *
 * Rules:
 *   DEVELOPER            -> companyName, else personal name
 *   AGENT with an agency -> Agency.name, else companyName, else personal name
 *   AGENT solo / OWNER   -> personal name
 *
 * Why AGENT falls back to `companyName` and not just `Agency.name`: an `Agency`
 * row can only be created from the dashboard, after KYC has produced an `Agent`
 * row. Someone who signs up as an agency therefore has no Agency for a while.
 * Capturing their name into `User.companyName` at signup means the right name
 * shows from day one, and the richer `Agency` record (logo, branded page, seats)
 * takes over the moment it exists.
 *
 * Note the deliberate asymmetry with KYC: verification is always of a PERSON
 * (a real ID and a selfie), and `KycSubmission.fullName` stays the legal name.
 * This helper only governs the public-facing display name. Admin screens keep
 * showing the personal name on purpose, because that is who is accountable.
 */

export interface SellerNameSource {
  name?: string | null
  role?: string | null
  companyName?: string | null
  /** From `include: { agent: { include: { agency: true } } }` on a User. */
  agent?: { agency?: { name?: string | null } | null } | null
  /** From a query that selected the agency directly. */
  agency?: { name?: string | null } | null
}

const FALLBACK = "Zim Estates seller"

export function sellerDisplayName(
  seller: SellerNameSource | null | undefined,
  fallback = FALLBACK,
): string {
  if (!seller) return fallback
  const personal = (seller.name ?? "").trim()

  if (seller.role === "DEVELOPER") {
    const company = (seller.companyName ?? "").trim()
    return company || personal || fallback
  }

  if (seller.role === "AGENT") {
    const agencyName = (seller.agency?.name ?? seller.agent?.agency?.name ?? "").trim()
    if (agencyName) return agencyName
    // Signed up as an agency but has not built the Agency record yet.
    const company = (seller.companyName ?? "").trim()
    if (company) return company
  }

  return personal || fallback
}

/**
 * True when the displayed name is an organisation rather than a person, so UI
 * can label it correctly ("Agency" vs "Agent") and skip person-only affordances
 * like initials-from-first-and-last-name avatars.
 */
export function isOrgSeller(seller: SellerNameSource | null | undefined): boolean {
  if (!seller) return false
  if (seller.role === "DEVELOPER") return !!(seller.companyName ?? "").trim()
  if (seller.role === "AGENT") {
    return !!((seller.agency?.name ?? seller.agent?.agency?.name ?? seller.companyName ?? "").trim())
  }
  return false
}

/** schema.org type for the seller, so listing JSON-LD does not label an agency a Person. */
export function sellerSchemaType(seller: SellerNameSource | null | undefined): "Organization" | "Person" {
  return isOrgSeller(seller) ? "Organization" : "Person"
}

/**
 * The person behind an organisation, for places that legitimately need it
 * (the agency's own dashboard, admin screens, KYC review). Never use this on a
 * public surface for an org seller.
 */
export function contactPersonName(seller: SellerNameSource | null | undefined): string {
  return (seller?.name ?? "").trim() || FALLBACK
}
